import React, { useState, useRef } from "react";
import { AppBar, Toolbar, Typography, Box } from "@mui/material";
import { ErrorBoundary } from "./services/logging";
import TradesTable from "./components/TradesTable";
import WalletSelector from "./components/WalletSelector";
import WalletDetails from "./components/WalletDetails";
import { WalletHistory } from "./components/WalletHistory";
import { UserManagement } from "./components/UserManagement";
import { WalletsPage } from "./components/WalletsPage";
import { TradesPage } from "./components/TradesPage";
import { Sidebar } from "./components/Sidebar";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Dashboard } from "./components/Dashboard";
import { UserEditDialog } from "./components/UserEditDialog";
import EventsCalendar from "./components/EventsCalendar";
import NotificationBell from "./components/NotificationBell";
import NotificationSettingsPage from "./components/NotificationSettingsPage";
import ValidationDemo from "./components/ValidationDemo";
import PnLDashboard from "./components/PnLDashboard";
import { ScenariosPage } from "./components/scenarios/ScenariosPage";
import { ScenarioForm } from "./components/scenarios/ScenarioForm";
import { PerformanceDashboard } from "./components/scenarios/performance/PerformanceDashboard";
import { MarketDashboard } from "./components/market-dashboard/MarketDashboard";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { LoginPage } from "./components/auth/LoginPage";
import { AuthCallback } from "./components/auth/AuthCallback";
import { UserProfileMenu } from "./components/auth/UserProfileMenu";
import { HealthIndicatorWidget } from "./components/AdminDashboard/HealthIndicator";
import { getEnv } from "./utils/getEnv";

function AppContent() {
  const [selectedWalletId, setSelectedWalletId] = useState<string | null>(null);
  const walletDetailsRef = useRef<{ refetchWallet: () => void } | null>(null);
  const walletHistoryRef = useRef<{ refetchHistory: () => void } | null>(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const { user } = useAuth();

  const rawRoles = (user as any)?.roles;
  const hasRoleMetadata = Array.isArray(rawRoles);
  const userRoles = hasRoleMetadata
    ? (rawRoles as string[]).map((role) => String(role).toUpperCase())
    : [];
  const isAdmin = hasRoleMetadata
    ? userRoles.includes("ADMIN") || userRoles.includes("SUPER_ADMIN")
    : true;

  const configuredHealthInterval = Number.parseInt(
    getEnv("HEALTH_CHECK_INTERVAL", "30000"),
    10,
  );
  const healthPollingInterval = Number.isFinite(configuredHealthInterval)
    ? configuredHealthInterval
    : 30000;

  const handleWalletChange = (walletId: string | null) => {
    setSelectedWalletId(walletId);
  };

  React.useEffect(() => {
    if (!selectedWalletId) {
      // This will be handled by the WalletSelector component
    }
  }, [selectedWalletId]);

  const refetchWallet = async () => {
    if (walletDetailsRef.current && walletDetailsRef.current.refetchWallet) {
      await walletDetailsRef.current.refetchWallet();
    }
    if (walletHistoryRef.current && walletHistoryRef.current.refetchHistory) {
      await walletHistoryRef.current.refetchHistory();
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Tradeventary
            </Typography>
            {user && (
              <>
                <WalletSelector
                  selectedWalletId={selectedWalletId}
                  onWalletChange={handleWalletChange}
                />
                <NotificationBell userId={user.id} />
              </>
            )}
            {user && <UserProfileMenu />}
            {user && isAdmin && (
              <Box sx={{ ml: 1, display: "flex", alignItems: "center" }}>
                <HealthIndicatorWidget
                  isAdmin={isAdmin}
                  pollingInterval={healthPollingInterval}
                />
              </Box>
            )}
          </Toolbar>
        </AppBar>
        <Box sx={{ flexGrow: 1, p: 1 }}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard walletId={selectedWalletId} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scenarios"
              element={
                <ProtectedRoute>
                  <ScenariosPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scenarios/create"
              element={
                <ProtectedRoute>
                  <ScenarioForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scenarios/performance"
              element={
                <ProtectedRoute>
                  <PerformanceDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wallets"
              element={
                <ProtectedRoute>
                  <WalletsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trades"
              element={
                <ProtectedRoute>
                  <TradesPage selectedWalletId={selectedWalletId} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pnl"
              element={
                <ProtectedRoute>
                  <PnLDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events"
              element={
                <ProtectedRoute>
                  <EventsCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  {/* ProtectedRoute guarantees a logged-in user; but tests render App without a logged-in user
            so keep this tolerant for unit-tests by falling back to empty string when user is missing. */}
                  <NotificationSettingsPage userId={user?.id || ""} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/validation-demo"
              element={
                <ProtectedRoute>
                  <ValidationDemo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/market"
              element={
                <ProtectedRoute>
                  <MarketDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Box>
        <UserEditDialog
          open={userDialogOpen}
          onClose={() => setUserDialogOpen(false)}
        />
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
